import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CalendarClock, Download, RefreshCcw, Search, Trash2, Split, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../supabase';
import { formatDateOnlyValue, getLocalDateKey } from '../../utils/localDateTime';
import {
  EXPIRY_STATUS_META,
  daysUntil,
  expiryAlertDays,
  expiryStatus,
  moverStockConLotes,
  reclasificarLote,
  validateLotesEntrada,
} from '../../utils/stockLotes';

// Inventario → Vencimientos (Vencimientos y lotes, fase 1).
// Lotes con stock por sucursal, semáforo, baja por vencimiento y reclasificación
// del "sin lote" (el stock que había al activar la función) en lotes con fecha.

const PAGE = 1000;
const fmt = (n) => Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 3 });
const money = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

const FILTROS = [
  { id: 'todos', label: 'Todos' },
  { id: 'vencido', label: 'Vencidos' },
  { id: 'critico', label: '≤ 7 días' },
  { id: 'proximo', label: 'Por vencer' },
  { id: 'sin_fecha', label: 'Sin fecha' },
];

const VencimientosPanel = ({ organization, sitios = [], siteId: siteIdInicial = 'global', onClose }) => {
  const alertDays = expiryAlertDays(organization);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteId, setSiteId] = useState(siteIdInicial || 'global');
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [baja, setBaja] = useState(null); // { lote, cantidad }
  const [reclas, setReclas] = useState(null); // { lote, partes: [{fecha_vencimiento, lote_codigo, cantidad}] }
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    if (!organization?.id) return;
    setLoading(true);
    try {
      const rows = [];
      for (let from = 0; ; from += PAGE) {
        let q = supabase
          .from('stock_lotes')
          .select('id, product_id, site_id, lote_codigo, fecha_vencimiento, cantidad_actual, costo_unitario, origen, created_at, products(name, barcode, brand, cost_price), sitios(nombre)')
          .eq('organization_id', organization.id)
          .gt('cantidad_actual', 0)
          .order('fecha_vencimiento', { ascending: true, nullsFirst: false })
          .range(from, from + PAGE - 1);
        if (siteId !== 'global') q = q.eq('site_id', siteId);
        const { data, error } = await q;
        if (error) throw error;
        rows.push(...(data || []));
        if (!data || data.length < PAGE) break;
      }
      setLotes(rows);
    } catch (error) {
      console.error('Vencimientos: no se pudieron cargar los lotes', error);
      toast.error('No se pudieron cargar los lotes');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, siteId]);

  useEffect(() => { cargar(); }, [cargar]);

  const hoy = getLocalDateKey();
  const conEstado = useMemo(() => lotes.map((l) => ({
    ...l,
    estado: expiryStatus(l.fecha_vencimiento, alertDays, hoy),
    dias: daysUntil(l.fecha_vencimiento, hoy),
    costo: Number(l.costo_unitario ?? l.products?.cost_price ?? 0),
  })), [lotes, alertDays, hoy]);

  const resumen = useMemo(() => {
    const r = { vencido: { u: 0, v: 0, n: 0 }, critico: { u: 0, v: 0, n: 0 }, proximo: { u: 0, v: 0, n: 0 }, sin_fecha: { u: 0, v: 0, n: 0 } };
    conEstado.forEach((l) => {
      if (!r[l.estado]) return;
      r[l.estado].u += Number(l.cantidad_actual) || 0;
      r[l.estado].v += (Number(l.cantidad_actual) || 0) * l.costo;
      r[l.estado].n += 1;
    });
    return r;
  }, [conEstado]);

  const visibles = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    return conEstado.filter((l) => {
      if (filtro !== 'todos' && l.estado !== filtro) return false;
      if (!term) return true;
      return [l.products?.name, l.products?.barcode, l.products?.brand, l.lote_codigo]
        .some((v) => String(v || '').toLowerCase().includes(term));
    });
  }, [conEstado, filtro, busqueda]);

  const confirmarBaja = async () => {
    const cant = Number(baja?.cantidad);
    if (!Number.isFinite(cant) || cant <= 0 || cant > Number(baja.lote.cantidad_actual)) {
      toast.error('Cantidad inválida');
      return;
    }
    setSaving(true);
    try {
      await moverStockConLotes({
        supabase,
        organizationId: organization.id,
        productId: baja.lote.product_id,
        siteId: baja.lote.site_id,
        delta: -cant,
        tipo: 'Egreso',
        motivo: `VENCIMIENTO${baja.lote.lote_codigo ? ` lote ${baja.lote.lote_codigo}` : ''}${baja.lote.fecha_vencimiento ? ` (${formatDateOnlyValue(baja.lote.fecha_vencimiento)})` : ''}`,
        lotes: [{ lote_id: baja.lote.id, cantidad: cant }],
        refTipo: 'baja_vencimiento',
        allowNegative: false,
      });
      toast.success('Baja registrada');
      setBaja(null);
      await cargar();
    } catch (error) {
      toast.error(error?.message || 'No se pudo dar de baja');
    } finally {
      setSaving(false);
    }
  };

  const confirmarReclas = async () => {
    const partes = (reclas?.partes || []).filter((p) => Number(p.cantidad) > 0 || p.fecha_vencimiento);
    const suma = partes.reduce((a, p) => a + (Number(p.cantidad) || 0), 0);
    if (partes.length === 0 || suma <= 0) { toast.error('Cargá al menos un lote con cantidad'); return; }
    if (suma > Number(reclas.lote.cantidad_actual) + 1e-9) { toast.error(`Hay ${fmt(reclas.lote.cantidad_actual)} u. para reclasificar`); return; }
    const v = validateLotesEntrada(partes, suma);
    if (!v.ok) { toast.error(v.error); return; }
    setSaving(true);
    try {
      await reclasificarLote({ supabase, loteId: reclas.lote.id, partes });
      toast.success('Lote reclasificado');
      setReclas(null);
      await cargar();
    } catch (error) {
      toast.error(error?.message || 'No se pudo reclasificar');
    } finally {
      setSaving(false);
    }
  };

  const exportar = async () => {
    if (!visibles.length) { toast.error('No hay lotes para exportar'); return; }
    let XLSX;
    try {
      const mod = await import('xlsx');
      XLSX = mod?.default || mod;
    } catch {
      toast.error('No se pudo cargar el exportador de Excel');
      return;
    }
    const rows = visibles.map((l) => ({
      Producto: l.products?.name || '',
      Código: l.products?.barcode || '',
      Sucursal: l.sitios?.nombre || '',
      Lote: l.lote_codigo || '',
      Vencimiento: formatDateOnlyValue(l.fecha_vencimiento),
      'Días': l.dias ?? '',
      Estado: EXPIRY_STATUS_META[l.estado]?.label || '',
      Cantidad: Number(l.cantidad_actual) || 0,
      'Costo unitario': l.costo,
      'Valor': (Number(l.cantidad_actual) || 0) * l.costo,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vencimientos');
    XLSX.writeFile(wb, `Vencimientos_${hoy}.xlsx`);
  };

  const tarjeta = (estado, titulo) => (
    <button
      type="button"
      onClick={() => setFiltro(filtro === estado ? 'todos' : estado)}
      className={`text-left bg-white p-3 rounded-2xl border shadow-sm transition-all ${filtro === estado ? 'border-[#51A7FD] ring-2 ring-[#51A7FD]/20' : 'border-slate-200'}`}
    >
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{titulo}</p>
      <p className="text-lg font-black text-slate-800">{fmt(resumen[estado].u)} <span className="text-[10px] text-slate-400">u.</span></p>
      <p className="text-[10px] font-bold text-slate-500">{resumen[estado].n} lotes · {money(resumen[estado].v)}</p>
    </button>
  );

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 px-1">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm" title="Volver a Inventario">
            <ArrowLeft size={18} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <CalendarClock size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Vencimientos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Lotes con stock · aviso desde {alertDays} días antes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={cargar} className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm">
            <RefreshCcw size={14} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
          <button onClick={exportar} className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm">
            <Download size={14} /> Exportar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {tarjeta('vencido', 'Vencidos')}
        {tarjeta('critico', 'Vencen en ≤ 7 días')}
        {tarjeta('proximo', `Por vencer (≤ ${alertDays} días)`)}
        {tarjeta('sin_fecha', 'Sin fecha (a reclasificar)')}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-3 flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar producto, código o lote…"
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold outline-none"
          />
        </div>
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-[11px] font-bold">
          <option value="global">Todas las sucursales</option>
          {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
        </select>
        <div className="flex gap-1 flex-wrap">
          {FILTROS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFiltro(f.id)}
              className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase ${filtro === f.id ? 'bg-[#0B2A36] text-white' : 'bg-slate-100 text-slate-500'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[720px]">
          <thead>
            <tr className="text-[9px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2">Producto</th>
              <th className="px-3 py-2">Lote</th>
              <th className="px-3 py-2">Vencimiento</th>
              <th className="px-3 py-2 text-right">Cantidad</th>
              <th className="px-3 py-2">Sucursal</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[10px] font-bold text-slate-400">Cargando…</td></tr>
            ) : visibles.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[10px] font-bold text-slate-400">No hay lotes para mostrar</td></tr>
            ) : visibles.map((l) => {
              const meta = EXPIRY_STATUS_META[l.estado];
              return (
                <tr key={l.id} className="border-b border-slate-50 text-[11px]">
                  <td className="px-3 py-2">
                    <div className="font-black uppercase text-slate-800">{l.products?.name || '—'}</div>
                    <div className="text-[9px] font-bold text-slate-400">{l.products?.barcode || ''}</div>
                  </td>
                  <td className="px-3 py-2 font-bold uppercase">{l.lote_codigo || (l.fecha_vencimiento ? '—' : 'Sin lote')}</td>
                  <td className="px-3 py-2 font-bold">
                    {l.fecha_vencimiento ? formatDateOnlyValue(l.fecha_vencimiento) : '—'}
                    {l.dias !== null && (
                      <span className="ml-1 text-[9px] text-slate-400">
                        {l.dias < 0 ? `(hace ${Math.abs(l.dias)} d)` : l.dias === 0 ? '(hoy)' : `(${l.dias} d)`}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-black">{fmt(l.cantidad_actual)}</td>
                  <td className="px-3 py-2 font-bold text-slate-500">{l.sitios?.nombre || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${meta.className}`}>{meta.label}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!l.fecha_vencimiento && (
                      <button
                        type="button"
                        onClick={() => setReclas({ lote: l, partes: [{ fecha_vencimiento: '', lote_codigo: '', cantidad: l.cantidad_actual }] })}
                        className="mr-1 px-2 py-1 rounded-lg bg-violet-50 text-violet-700 border border-violet-100 text-[9px] font-black uppercase inline-flex items-center gap-1"
                        title="Asignar vencimiento a este stock"
                      >
                        <Split size={11} /> Asignar fecha
                      </button>
                    )}
                    {/* La baja por vencimiento es para lo vencido o por vencer; otra
                        salida (rotura, consumo) va por Ajustes de stock. */}
                    {['vencido', 'critico', 'proximo'].includes(l.estado) && (
                      <button
                        type="button"
                        onClick={() => setBaja({ lote: l, cantidad: l.cantidad_actual })}
                        className="px-2 py-1 rounded-lg bg-rose-50 text-rose-700 border border-rose-100 text-[9px] font-black uppercase inline-flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Dar de baja
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {baja && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setBaja(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black uppercase text-slate-800">Baja por vencimiento</h3>
              <button onClick={() => setBaja(null)} className="text-slate-400"><X size={16} /></button>
            </div>
            <p className="text-[11px] font-bold text-slate-600 mb-3">
              {baja.lote.products?.name} · {baja.lote.lote_codigo || 'sin código'}
              {baja.lote.fecha_vencimiento ? ` · vence ${formatDateOnlyValue(baja.lote.fecha_vencimiento)}` : ''}
              {' '}· disponible {fmt(baja.lote.cantidad_actual)} u.
            </p>
            <label className="text-[9px] font-black uppercase text-slate-400">Cantidad a dar de baja</label>
            <input
              type="number"
              min="0"
              max={baja.lote.cantidad_actual}
              step="any"
              value={baja.cantidad}
              onChange={(e) => setBaja({ ...baja, cantidad: e.target.value })}
              className="w-full mt-1 mb-4 px-3 py-2 border border-slate-200 rounded-xl text-sm font-black text-right"
            />
            <button
              type="button"
              disabled={saving}
              onClick={confirmarBaja}
              className="w-full py-2.5 rounded-xl bg-rose-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? 'Registrando…' : 'Confirmar baja'}
            </button>
          </div>
        </div>
      )}

      {reclas && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setReclas(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-black uppercase text-slate-800">Asignar vencimiento</h3>
              <button onClick={() => setReclas(null)} className="text-slate-400"><X size={16} /></button>
            </div>
            <p className="text-[11px] font-bold text-slate-600 mb-3">
              {reclas.lote.products?.name} · {fmt(reclas.lote.cantidad_actual)} u. sin fecha. Partilo según lo que contaste; lo que no asignes queda sin fecha.
            </p>
            <div className="flex flex-col gap-2">
              {reclas.partes.map((p, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <input
                    type="date"
                    value={p.fecha_vencimiento}
                    onChange={(e) => setReclas({ ...reclas, partes: reclas.partes.map((x, j) => (j === i ? { ...x, fecha_vencimiento: e.target.value } : x)) })}
                    className="h-9 px-2 border border-slate-200 rounded-lg text-[11px] font-bold"
                  />
                  <input
                    type="text"
                    placeholder="Lote"
                    value={p.lote_codigo}
                    onChange={(e) => setReclas({ ...reclas, partes: reclas.partes.map((x, j) => (j === i ? { ...x, lote_codigo: e.target.value.toUpperCase() } : x)) })}
                    className="h-9 w-24 px-2 border border-slate-200 rounded-lg text-[11px] font-bold uppercase"
                  />
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={p.cantidad}
                    onChange={(e) => setReclas({ ...reclas, partes: reclas.partes.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)) })}
                    className="h-9 w-20 px-2 border border-slate-200 rounded-lg text-[11px] font-bold text-right"
                  />
                  {reclas.partes.length > 1 && (
                    <button onClick={() => setReclas({ ...reclas, partes: reclas.partes.filter((_, j) => j !== i) })} className="text-slate-300 hover:text-rose-500"><X size={14} /></button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setReclas({ ...reclas, partes: [...reclas.partes, { fecha_vencimiento: '', lote_codigo: '', cantidad: '' }] })}
              className="mt-2 text-[9px] font-black uppercase text-violet-700"
            >
              + Otro vencimiento
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={confirmarReclas}
              className="w-full mt-4 py-2.5 rounded-xl bg-[#0B2A36] text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VencimientosPanel;
